// PolicyReport.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { FaFilePdf, FaRegFilePdf } from 'react-icons/fa';
import { PiMicrosoftExcelLogoBold } from 'react-icons/pi';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../../../services/config';

// libs for client-side export
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---- RTK hooks (adjust import path to where you re-exported them) ----
import {
  useGetPolicyReportQuery,
  useLazyPreviewPolicyReportQuery,
  useLazyPreviewPremiumDueReportQuery,
  useLazyPreviewLapsedPoliciesReportQuery,
  useLazyPreviewPremiumOutstandingReportQuery,
  useExportPolicyReportMutation,
  useGetExportStatusQuery,
  useDownloadExportQuery,
} from '../../../../../services/redux/api'; // <-- fixed path
import Loader from '../../../../../utils/Loader';
import ReportLoader from './ReportLoader';
import { RiFileExcel2Line } from 'react-icons/ri';
import fetchPolicyReport from '../../../../../utils/fetchPolicyReport';

const BASE_SORT_OPTIONS = [
  { key: 'groupwise', label: 'Group' },
  { key: 'memberwise', label: 'Member' },
  { key: 'areawise', label: 'Area' },
  { key: 'policywise', label: 'Policy No' },
];

function groupBy(array, key) {
  return array.reduce((acc, item) => {
    const k = item[key] ?? 'Unknown';
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}

/**
 * Normalize mutation/query hook shapes:
 * - RTK-toolkit codegen often returns: [trigger, result]
 * - Some setups return an object: { trigger, data, isLoading, error, ... }
 * This helper returns { trigger, result } consistently.
 */
function normalizeHook(hook) {
  // default
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    // [trigger, result]
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    // object shape: { trigger/mutate, data, isLoading, error, status }
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading ?? hook.isFetching ?? hook.status === 'pending',
      error: hook.error,
      status: hook.status,
    };
  } else {
    trigger = undefined;
    result = {};
  }

  return { trigger, result };
}

export default function PolicyReport({ data: initialData = [], initialSort = 'groupwise', includePolicyStatus = false, includeMaturityDate = false, headerTitle = 'Policy Report', extraSortOptions = [], includePremiumDueDate = false, includePremiumOutstandingDate = false, includeLapsedDate = false, includeFUPDate = false, reportType = null, fromDate = null, toDate = null, fupFrom = null, fupTo = null }) {
  const location = useLocation();
  const payloadFromNav = location?.state?.payload ?? null;

  const [sortMode, setSortMode] = useState(initialSort);
  const [expanded, setExpanded] = useState({});
  // local state used by UI for preview modal / jobs
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [overrideData, setOverrideData] = useState(null);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [pdfIsDummy, setPdfIsDummy] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatusText, setJobStatusText] = useState('');
  const [jobCreating, setJobCreating] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // track whether an auth token exists locally so we can guard preview/export UI
  const [hasAuthToken, setHasAuthToken] = useState(false);

  // build params helper (same logic as before)
  const buildQueryParamsFromPayload = (p) => {
    if (!p) return {};
    const params = {};
    if (p.pageNumber) params.pageNumber = p.pageNumber;
    if (p.pageSize) params.pageSize = p.pageSize;
    if (p.fromDate) params.fromDate = p.fromDate;
    if (p.toDate) params.toDate = p.toDate;
    if (p.paymentMode) params.paymentMode = p.paymentMode;
    if (p.policyStatus) params.policyStatus = p.policyStatus;
    if (p.groupIds) params.groupIds = p.groupIds;
    if (p.sortBy) params.sortBy = p.sortBy;
    if (typeof p.includeAddress !== 'undefined') params.includeAddress = p.includeAddress ? '1' : '0';
    if (typeof p.includeMobile !== 'undefined') params.includeMobile = p.includeMobile ? '1' : '0';
    if (typeof p.includeEmail !== 'undefined') params.includeEmail = p.includeEmail ? '1' : '0';
    if (p.policyNumber) params.policyNumber = p.policyNumber;
    if (p.searchValue) params.searchValue = p.searchValue;
    if (p.filterByDate) {
      // normalize common synonyms to the backend-expected values
      const raw = String(p.filterByDate || '').toLowerCase();
      if (raw.includes('fup') || raw.includes('fupdate') || raw.includes('fupdate')) params.filterByDate = 'fup';
      else if (raw.includes('comm') || raw.includes('commence') || raw.includes('commencement')) params.filterByDate = 'comm';
      else params.filterByDate = raw;
    }
    if (typeof p.includeLapsed !== 'undefined') params.includeLapsed = p.includeLapsed ? '1' : '0';
    return params;
  };

  // Parameters used to fetch the list shown on the page. These should prefer
  // the original navigation payload (so the page reflects filters applied by
  // the caller) and NOT be driven by transient UI sortMode to preserve the
  // existing client-side sorting UX (clicking buttons sorts locally).
  const fetchParams = useMemo(() => {
    const base = buildQueryParamsFromPayload(payloadFromNav || {});
    const usesFupProp = includePremiumDueDate || includePremiumOutstandingDate || includeLapsedDate || includeFUPDate;
    if (usesFupProp && !base.filterByDate) base.filterByDate = 'fup';
    // if explicit fupFrom/fupTo props were provided, prefer them over navigation payload
    if (fupFrom) base.fromDate = fupFrom;
    if (fupTo) base.toDate = fupTo;
    // carry through any report identity coming from navigation (do not inject UI sort here)
    try {
      if (headerTitle) base.reportTitle = headerTitle;
      if (reportType) base.reportType = reportType;
    } catch (e) {
      // ignore
    }
    // keep any incoming sort tokens from navigation but DO NOT overwrite with UI sortMode
    if (payloadFromNav && payloadFromNav.sortingOrder) base.sortingOrder = payloadFromNav.sortingOrder;
    if (payloadFromNav && payloadFromNav.sortBy) base.sortBy = payloadFromNav.sortBy;
    return base;
  }, [payloadFromNav, includePremiumDueDate, includePremiumOutstandingDate, includeLapsedDate, includeFUPDate, fupFrom, fupTo, headerTitle, reportType]);

  // If the parent passed reportType + fromDate/toDate props, fetch using the helper
  useEffect(() => {
    let mounted = true;
    const shouldUseProps = reportType && (fromDate || toDate);
    if (!shouldUseProps) {
      setOverrideData(null);
      setOverrideLoading(false);
      return () => { mounted = false; };
    }
    setOverrideLoading(true);
    fetchPolicyReport(reportType, fromDate, toDate)
      .then((res) => {
        if (!mounted) return;
        const rows = Array.isArray(res) ? res : (res?.data ?? res?.rows ?? []);
        setOverrideData(rows);
      })
      .catch((err) => {
        console.error('fetchPolicyReport error', err);
        if (mounted) setOverrideData([]);
      })
      .finally(() => { if (mounted) setOverrideLoading(false); });

    return () => { mounted = false; };
  }, [reportType, fromDate, toDate]);

  /* =========================
     1) Fetch report list (RTK Query)
     ========================= */
  // Use fetchParams for listing the table; keep UI sortMode local so clicks
  // immediately re-order the visible table without forcing a server round-trip.
  const {
    data: apiReportResponse,
    isLoading: loadingData,
    isError: dataIsError,
    error: dataError,
    refetch: refetchReport,
  } = useGetPolicyReportQuery(fetchParams, { skip: false });
  
  // if overrideData is provided (via reportType/fromDate/toDate props), prefer it
  // apiReportResponse comes from RTK query otherwise

  // normalize incoming API data to the same shape used in UI (keeps old code intact)
  const rawDataSource = overrideData !== null ? overrideData : apiReportResponse;
  const rawData = Array.isArray(rawDataSource) ? rawDataSource : (rawDataSource?.data ?? rawDataSource ?? initialData ?? []);

  const data = rawData.map((r) => {
    // Extract group code and name, handling cases where GroupCode might already include the name
    const rawGroupCode = r.GroupCode ?? r.groupCode ?? r.Group ?? 'Unknown';
    const rawGroupName = r.GroupName ?? r.groupName ?? r.Group_Name ?? '';
    
    // Extract the actual group code (e.g., "GRP100" from "GRP100 - Ashmit - Imported")
    let groupCode = rawGroupCode;
    let groupName = rawGroupName;
    
    if (typeof rawGroupCode === 'string' && rawGroupCode.includes(' - ')) {
      // Split by " - " and take only the first part as the group code
      const parts = rawGroupCode.split(' - ');
      groupCode = parts[0].trim(); // e.g., "GRP100"
      
      // If groupName is not provided separately, use the second part only (not all parts)
      if (!rawGroupName && parts.length > 1) {
        groupName = parts[1].trim(); // e.g., "Ashmit" (just the group name, not member name)
      }
    }
    
    return {
      groupCode,
      groupName,
      lifeAssured: r.LifeAssured ?? r.LifeAssuredRaw ?? r.lifeAssured ?? r.LifeAssuredName ?? '',
    policyNo: r.PolicyNo ?? r.policyNo ?? r.PolicyNumber ?? r.PolicyNo ?? '',
    policyStatus: r.PolicyStatus ?? r.Policy_Status ?? r.PolicyStatusName ?? r.policyStatus ?? r.Status ?? (r.__raw && (r.__raw.PolicyStatus ?? r.__raw.Status)) ?? '',
    maturityDate: r.MaturityDate ?? r.maturityDate ?? r.MaturityDateStr ?? r.maturityDateStr ?? (r.__raw && (r.__raw.MaturityDate ?? r.__raw.MaturityDateStr)) ?? '',
    MaturityDateObj: r.MaturityDateObj ?? r.MaturityDateObj ?? (r.__raw && r.__raw.MaturityDateObj) ?? null,
    // maturityStatus removed from UI exports (Policy Status column is used instead)
    maturityStatus: r.MaturityStatus ?? r.maturityStatus ?? (r.__raw && r.__raw.MaturityStatus) ?? '',
    product: r.Product ?? r.product ?? r.ProductName ?? '',
    termPPT: r.TermPPT ?? r.termPPT ?? r.Term ?? r.Term ?? '',
    commencementDate: r.CommDate ?? r.commencementDate ?? r.CommencementDate ?? r.CommencementDate ?? '',
    fupDate: r.FUPDate ?? r.fupDate ?? r.FUPDate ?? r.FUPDate ?? '',
    sumAssured: r.SumAssured ?? r.sumAssured ?? (r.Sum_Assured ?? '') ?? '',
    paymentMode: r.PaymentMode ?? r.paymentMode ?? '',
    netPremium: r.NetPremium ?? r.netPremium ?? r.NetPremium ?? '',
    agency: r.Agency ?? r.agency ?? '',
    address: r.Address ?? r.address ?? (r.__raw && (r.__raw.Address ?? r.__raw.address)) ?? '',
    mobile: r.Mobile ?? r.mobile ?? (r.__raw && (r.__raw.Mobile ?? r.__raw.mobile)) ?? '',
    email: r.Email ?? r.email ?? (r.__raw && (r.__raw.Email ?? r.__raw.email)) ?? '',
    area: (r.raw && r.raw.AreaName) ?? r.AreaName ?? r.Area ?? r.area ?? (r.__raw && (r.__raw.AreaName ?? r.__raw.Area ?? r.__raw.area)) ?? '',
    __raw: r,
  }
  });   

  // Client-side safety filter: if reportType + from/to provided OR previewParams asks for FUP filtering, ensure FUPDate lies between the range
  const filteredByFup = useMemo(() => {
    const wantsFup = ((reportType && (fromDate || toDate)) || (fetchParams && String(fetchParams.filterByDate || '').toLowerCase() === 'fup'));
    if (!wantsFup) return data;
    const toTime = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    // prefer explicit props fromDate/toDate, fall back to previewParams if props are not provided
  const fVal = fromDate || fetchParams.fromDate || fetchParams.fupFrom || null;
  const tVal = toDate || fetchParams.toDate || fetchParams.fupTo || null;
    const f = fVal ? toTime(fVal) : null;
    const t = tVal ? toTime(tVal) : null;
    return data.filter((row) => {
      const candidate = row.fupDate || row.__raw?.FUPDate || row.__raw?.fupDate || row.__raw?.FupDate || '';
      const ct = toTime(candidate);
      if (f !== null && (ct === null || ct < f)) return false;
      if (t !== null && (ct === null || ct > t)) return false;
      return true;
    });
  }, [data, reportType, fromDate, toDate, fetchParams]);

  const finalData = (reportType && (fromDate || toDate)) ? filteredByFup : data;

  // build sort options by merging base options with any extra options the caller passed
  const SORT_OPTIONS = useMemo(() => {
    const seen = new Set(BASE_SORT_OPTIONS.map(o => o.key));
    const merged = [...BASE_SORT_OPTIONS];
    
    // Add Product sorting only for the main Policy Report
    if (headerTitle === 'Policy Report') {
      merged.splice(3, 0, { key: 'productwise', label: 'Product' });
      seen.add('productwise');
    }
    
    (extraSortOptions || []).forEach(o => {
      if (o && o.key && !seen.has(o.key)) {
        merged.push({ key: o.key, label: o.label || o.key });
        seen.add(o.key);
      }
    });
    return merged;
  }, [extraSortOptions, headerTitle]);

  // If this is the Maturity Policies report, restrict display to policies whose status is 'matured'
  // Detection: includeMaturityDate flag OR reportType/headerTitle mention OR payload contains maturity date range
  const isMaturityReport = Boolean(
    includeMaturityDate ||
      (reportType && String(reportType).toLowerCase().includes('matur')) ||
      (headerTitle && String(headerTitle).toLowerCase().includes('matur')) ||
  (payloadFromNav && (payloadFromNav.fromMaturityDate || payloadFromNav.toMaturityDate)) ||
  (fetchParams && (fetchParams.fromMaturityDate || fetchParams.toMaturityDate))
  );

  const displayData = useMemo(() => {
    if (!isMaturityReport) return finalData;
    // normalize date helpers
    const toTime = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };

    const fVal = payloadFromNav?.fromMaturityDate || fromDate || null;
    const tVal = payloadFromNav?.toMaturityDate || toDate || null;
    const f = fVal ? toTime(fVal) : null;
    const t = tVal ? toTime(tVal) : null;

    const isMatureStatus = (st) => {
      if (!st && st !== 0) return false;
      const s = String(st).toLowerCase();
      // match words like 'mature', 'matured', 'maturity' (word boundary)
      return /\bmatur/i.test(s);
    };

    return finalData.filter((row) => {
      const status = row.policyStatus ?? (row.__raw && (row.__raw.PolicyStatus || row.__raw.Status)) ?? '';
      if (!isMatureStatus(status)) return false; // only matured policies
      // apply maturity date range if provided
      if (f !== null || t !== null) {
        const candidate = row.maturityDate || row.__raw?.MaturityDate || row.__raw?.maturityDate || '';
        const ct = toTime(candidate);
        if (f !== null && (ct === null || ct < f)) return false;
        if (t !== null && (ct === null || ct > t)) return false;
      }
      return true;
    });
  }, [finalData, includeMaturityDate, payloadFromNav, fromDate, toDate, reportType]);

  const grouped = useMemo(() => {
    return displayData.reduce((acc, item) => {
      const key = `${item.groupCode} - ${item.groupName || 'Unknown'}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [displayData]);

  // include flags from payload (these come from LifePolicyRegister payload)
  const includeAddress = fetchParams.includeAddress === '1' || fetchParams.includeAddress === 1 || fetchParams.includeAddress === true;
  const includeMobile = fetchParams.includeMobile === '1' || fetchParams.includeMobile === 1 || fetchParams.includeMobile === true;
  const includeEmail = fetchParams.includeEmail === '1' || fetchParams.includeEmail === 1 || fetchParams.includeEmail === true;

  const extraCols = (includeAddress ? 1 : 0) + (includeMobile ? 1 : 0) + (includeEmail ? 1 : 0);
  const baseCols = 12 + (includePolicyStatus ? 1 : 0) + (includeMaturityDate ? 1 : 0);
  const totalCols = baseCols + extraCols;

  const flatSorted = useMemo(() => {
    const copy = [...displayData];

    const toTime = (val) => {
      if (!val) return 0;
      // val might be a Date object string, ISO, or a display string — try Date parse
      const d = new Date(val);
      const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
      return t;
    };

    const compareStr = (a, b, key) => ((a?.[key] || '') + '').localeCompare((b?.[key] || '') + '');

    switch (sortMode) {
      case 'memberwise':
        copy.sort((a, b) => compareStr(a, b, 'lifeAssured'));
        break;
      case 'productwise':
        copy.sort((a, b) => compareStr(a, b, 'product'));
        break;
      case 'termwise':
        copy.sort((a, b) => compareStr(a, b, 'termPPT'));
        break;
      case 'areawise':
        copy.sort((a, b) => compareStr(a, b, 'area'));
        break;
      case 'maturity':
        // use MaturityDateObj if available (ISO/string) or maturityDate
        copy.sort((a, b) => toTime(a.__raw?.MaturityDateObj ?? a.MaturityDateObj ?? a.maturityDate) - toTime(b.__raw?.MaturityDateObj ?? b.MaturityDateObj ?? b.maturityDate));
        break;
      case 'fup':
        copy.sort((a, b) => toTime(a.__raw?.FUPDate ?? a.FupDate) - toTime(b.__raw?.FUPDate ?? b.FupDate));
        break;
      case 'policywise':
        // numeric-first then lexical
        copy.sort((a, b) => {
          const na = parseInt((a.policyNo || '').replace(/\D/g, ''), 10);
          const nb = parseInt((b.policyNo || '').replace(/\D/g, ''), 10);
          if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
          return ((a.policyNo || '') + '').localeCompare((b.policyNo || '') + '');
        });
        break;
      case 'commencement':
        copy.sort((a, b) => toTime(a.commencementDate) - toTime(b.commencementDate));
        break;
      default:
        copy.sort((a, b) => compareStr(a, b, 'groupCode'));
        break;
    }

    return copy;
  }, [displayData, sortMode]);

  const toggleGroup = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  /* =========================
    2) Preview PDF via RTK lazy query - dynamically select based on reportType
    ========================= */
  // Call all hooks unconditionally (React hooks rules)
  const [triggerPolicyReport, policyReportResult] = useLazyPreviewPolicyReportQuery();
  const [triggerPremiumDue, premiumDueResult] = useLazyPreviewPremiumDueReportQuery();
  const [triggerLapsed, lapsedResult] = useLazyPreviewLapsedPoliciesReportQuery();
  const [triggerOutstanding, outstandingResult] = useLazyPreviewPremiumOutstandingReportQuery();
  
  // Select the appropriate hook based on reportType
  let triggerPreview, previewResult;
  switch (reportType) {
    case 'PremiumDueReport':
      triggerPreview = triggerPremiumDue;
      previewResult = premiumDueResult;
      break;
    case 'LapsedReport':
      triggerPreview = triggerLapsed;
      previewResult = lapsedResult;
      break;
    case 'OutstandingReport':
      triggerPreview = triggerOutstanding;
      previewResult = outstandingResult;
      break;
    default:
      triggerPreview = triggerPolicyReport;
      previewResult = policyReportResult;
      break;
  }

  // subscribe to auth token in Redux so UI updates when user logs in/out
  const reduxToken = useSelector((s) => s.auth && (s.auth.token || s.auth?.auth?.token));

  useEffect(() => {
    if (reduxToken) {
      setHasAuthToken(true);
      return;
    }

    try {
      const raw = localStorage.getItem('auth');
      if (raw) {
        const parsed = JSON.parse(raw || '{}');
        if (parsed && parsed.token) {
          setHasAuthToken(true);
          return;
        }
      }
    } catch (err) {
      // ignore parse errors
    }

    const rawToken = localStorage.getItem('token');
    setHasAuthToken(!!rawToken);
  }, [reduxToken]);

  // map incoming navigation payload sortBy to local sortMode so server-driven sort is reflected
  useEffect(() => {
    const s = (payloadFromNav?.sortBy ?? payloadFromNav?.sort ?? payloadFromNav?.sortingOrder) || fetchParams.sortBy || fetchParams.sort || fetchParams.sortingOrder;
    if (!s) return;
    const v = (s + '').toLowerCase();
    if (v.includes('group')) return setSortMode('groupwise');
    if (v.includes('member')) return setSortMode('memberwise');
    if (v.includes('area')) return setSortMode('areawise');
    if (v.includes('maturity')) return setSortMode('maturity');
    if (v.includes('fup')) return setSortMode('fup');
    if (v.includes('product')) return setSortMode('productwise');
    if (v.includes('term') || v.includes('ppt')) return setSortMode('termwise');
    if (v.includes('policy') || v.includes('policyno') || v.includes('policy_no')) return setSortMode('policyno');
    if (v.includes('commence') || v.includes('commencement')) return setSortMode('commencement');
  }, [payloadFromNav, fetchParams]);

  const handlePreviewPdf = async (e) => {
    e?.preventDefault?.();
    try {
      setPreviewLoading(true);
      // debug: log the params we're about to send for preview
      // eslint-disable-next-line no-console
      // build params for preview/export: include UI sortMode and report identity
      const exportParams = { ...fetchParams };
      try { if (headerTitle) exportParams.reportTitle = headerTitle; } catch {};
      try { if (reportType) exportParams.reportType = reportType; } catch {};
      // prefer the active UI sortMode (user clicks) when creating preview/export
      if (sortMode) {
        exportParams.sortBy = sortMode;
        exportParams.sortingOrder = sortMode;
      }
      console.debug('[PolicyReport] preview/export params ->', exportParams);
      const p = triggerPreview(exportParams);
      let res;
      if (p && typeof p.unwrap === 'function') {
        res = await p.unwrap();
      } else {
        res = await p;
      }

      const payload = (res && (res.data ?? res)) || previewResult?.data;
      const final = payload || (previewResult?.data ?? null);
      if (final && final.pdf) {
        const base64 = final.pdf;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfObjectUrl(url);
        setPdfIsDummy(!!final.isDummy);
        setShowPdfModal(true);
        setPreviewLoading(false);
      }
    } catch (err) {
      setPreviewLoading(false);
      console.error('Preview trigger error', err);
      // if server returned diagnostic info (404 with counts), show it to the user
      const info = err && err.data ? err.data : (err && err.error ? err.error : null);
      if (info) {
        const msg = info.message || 'Preview failed';
        const rawCount = info.rawCount ?? info.raw_count ?? null;
        const enrichedCount = info.enrichedCount ?? null;
        const filteredCount = info.filteredCount ?? info.filtered_count ?? null;
        alert(`${msg}${rawCount !== null ? `\nraw: ${rawCount}` : ''}${enrichedCount !== null ? `\nenriched: ${enrichedCount}` : ''}${filteredCount !== null ? `\nfiltered: ${filteredCount}` : ''}`);
      } else {
        if (!previewResult?.data) alert('Failed to start preview. Check console for details.');
      }
    }
  };

  // when previewResult result arrives, convert base64 -> blob and open modal (covers delayed responses)
  useEffect(() => {
    const res = previewResult?.data;
    if (!res) return;
    try {
      const payload = res?.data ?? res;
      if (payload && payload.pdf) {
        const base64 = payload.pdf;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfObjectUrl(url);
        setPdfIsDummy(!!payload.isDummy);
        setShowPdfModal(true);
        setPreviewLoading(false);
      } else {
        // show diagnostic info if available
        const msg = payload?.message ?? 'Preview generation failed. No PDF returned.';
        const rawCount = payload?.rawCount ?? null;
        const enrichedCount = payload?.enrichedCount ?? null;
        const filteredCount = payload?.filteredCount ?? null;
        alert(`${msg}${rawCount !== null ? `\nraw: ${rawCount}` : ''}${enrichedCount !== null ? `\nenriched: ${enrichedCount}` : ''}${filteredCount !== null ? `\nfiltered: ${filteredCount}` : ''}`);
        setPreviewLoading(false);
      }
    } catch (err) {
      console.error('Preview conversion error', err);
      alert('Failed to generate preview. Check console for details.');
      setPreviewLoading(false);
    }
  }, [previewResult?.data]);

  /* =========================
     3) Create export job (RTK mutation) + poll status (RTK query)
     ========================= */
  const [createExportJob, createJobResult] = useExportPolicyReportMutation();

  // job status poll (assumed query hook)
  const { data: jobStatusData } = useGetExportStatusQuery(jobId, { pollingInterval: 2000, skip: !jobId });

  useEffect(() => {
    if (jobStatusData?.job) {
      const j = jobStatusData.job;
      setJobStatusText(j.status + (j.error ? ` — ${j.error}` : ''));
      if (j.status === 'done' && j.filePath) {
        // download
        downloadGeneratedFile(jobId).catch((e) => console.error('download error', e));
      } else if (j.status === 'failed') {
        setJobCreating(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatusData]);

  // download hook
  const { refetch: triggerDownload } = useDownloadExportQuery(jobId, { skip: true });

  const downloadGeneratedFile = async (id) => {
    try {
      if (triggerDownload) {
        const dlRes = await triggerDownload();
        const blob = dlRes?.data;
        if (blob instanceof Blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `policy_report_${id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } else if (dlRes?.url) {
          window.location.href = dlRes.url;
        } else {
          window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
        }
      } else {
        window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
      }
    } catch (err) {
      console.error('Error downloading file', err);
      window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
    }
  };

  const handleCreateExportJobAndDownload = async () => {
    setJobCreating(true);
    setJobStatusText('Creating export job...');
    try {
      // If we already have a PDF preview object URL, download it directly to avoid creating a job
      if (pdfObjectUrl) {
        const filename = `policy_report_preview_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
        const a = document.createElement('a');
        a.href = pdfObjectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setJobCreating(false);
        setJobStatusText('Downloaded preview PDF');
        return;
      }
  if (typeof createExportJob !== 'function') throw new Error('Create job hook not available');
  // Build export params separately so we include current UI sortMode but
  // don't alter the table fetch behavior.
  const exportParams = { ...fetchParams };
  try { if (headerTitle) exportParams.reportTitle = headerTitle; } catch {};
  try { if (reportType) exportParams.reportType = reportType; } catch {};
  if (sortMode) { exportParams.sortBy = sortMode; exportParams.sortingOrder = sortMode; }
  exportParams.format = 'pdf';
  const res = await createExportJob(exportParams).unwrap();
      const payload = res?.data ?? res;
      const newJobId = payload?.jobId ?? payload?.job?.id ?? payload?.id;
      if (!newJobId) throw new Error('No jobId returned from create export job');
      setJobId(newJobId);
      setJobStatusText(`Job created: ${newJobId}. Waiting for file...`);
      // polling will handle the rest
    } catch (err) {
      console.error('Export job error', err);
      alert('Export job failed: ' + (err?.message ?? err));
      setJobCreating(false);
    }
  };

  /* =========================
     4) Fallback client-side XLSX/PDF exports retained
     ========================= */
  const exportToXlsx = async (rows) => {
  const includeAddress = fetchParams.includeAddress === '1' || fetchParams.includeAddress === 1 || fetchParams.includeAddress === true;
  const includeMobile = fetchParams.includeMobile === '1' || fetchParams.includeMobile === 1 || fetchParams.includeMobile === true;
  const includeEmail = fetchParams.includeEmail === '1' || fetchParams.includeEmail === 1 || fetchParams.includeEmail === true;

    // build a union of keys across all rows (and nested __raw) so XLSX contains every field
    const keySet = new Set();
    rows.forEach(r => {
      if (!r || typeof r !== 'object') return;
      Object.keys(r).forEach(k => { if (k === '__raw') return; keySet.add(k); });
      if (r.__raw && typeof r.__raw === 'object') Object.keys(r.__raw).forEach(k => keySet.add(k));
    });
    // preferred ordering for readability
    const preferred = ['GroupCode', 'Area', 'LifeAssured', 'LifeAssuredName', 'PolicyNo', 'PolicyNumber', 'PolicyStatus', 'Product', 'TermPPT', 'CommencementDate', 'CommDate', 'FUPDate', 'MaturityDate', 'SumAssured', 'PaymentMode', 'NetPremium', 'Agency', 'Address', 'Mobile', 'Email', 'DOB', 'CustomerID'];
    const ordered = [];
    preferred.forEach(k => { if (keySet.has(k)) { ordered.push(k); keySet.delete(k); } });
    const rest = Array.from(keySet).sort();
    const finalKeys = ordered.concat(rest);

    const humanize = (k) => {
      if (!k) return '';
      let s = String(k).replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
      s = s.replace(/\s+/g, ' ').trim();
      return s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const headerKeys = finalKeys.map(k => humanize(k));

    const dataRows = rows.map((r) => {
      return finalKeys.map(k => {
        if (r && Object.prototype.hasOwnProperty.call(r, k)) return r[k];
        if (r && r.__raw && Object.prototype.hasOwnProperty.call(r.__raw, k)) return r.__raw[k];
        // case-insensitive fallbacks for common normalized keys
        const lower = k.toLowerCase();
        if (r) {
          for (const rk of Object.keys(r)) if (rk.toLowerCase() === lower) return r[rk];
        }
        if (r && r.__raw) {
          for (const rk of Object.keys(r.__raw)) if (rk.toLowerCase() === lower) return r.__raw[rk];
        }
        return '';
      });
    });          //updated it to add the areaName as well

    // create workbook and worksheet with ExcelJS
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Policy Report');

      // set columns with widths based on header labels
      ws.columns = headerKeys.map((h) => ({ header: h, key: h, width: Math.max(12, Math.min(40, h.length + 8)) }));

      // add header row (ExcelJS already adds the header when columns are defined)
      const headerRow = ws.getRow(1);
      headerRow.height = 24;
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.font = { name: 'Arial', size: 14, bold: true };
      headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF666666' } },
          left: { style: 'thin', color: { argb: 'FF666666' } },
          bottom: { style: 'thin', color: { argb: 'FF666666' } },
          right: { style: 'thin', color: { argb: 'FF666666' } },
        };
      });

      // append data rows
      dataRows.forEach((r) => ws.addRow(r));

      // autosize small columns a bit more
      ws.columns.forEach((col) => {
        if (!col.width || col.width < 12) col.width = 12;
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const filename = `policy_report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
      saveAs(blob, filename);
    } catch (err) {
      // fallback: if ExcelJS is not available for any reason, fallback to simple SheetJS export
      // eslint-disable-next-line no-console
      console.debug('ExcelJS export failed, falling back to simple JSON-to-sheet', err?.message ?? err);
      try {
        // previous simple fallback using SheetJS
        const worksheetData = rows.map((r) => {
          const base = {
            'Group Code': r.groupCode,
            'Area': r.__raw?.Area ?? r.area ?? '',
            'Life Assured': r.lifeAssured,
            'Policy No': r.policyNo,
            ...(includePolicyStatus ? { 'Policy Status': r.policyStatus } : {}),
            ...(includeMaturityDate ? { 'Maturity Date': r.maturityDate } : {}),
            Product: r.product,
            'Term/PPT': r.termPPT,
            'Commencement Date': r.commencementDate,
            'FUP Date': r.fupDate,
            'Sum Assured': r.sumAssured,
            'Payment Mode': r.paymentMode,
            'Net Premium': r.netPremium,
            Agency: r.agency,
          };
          if (includeAddress) base['Address'] = r.__raw?.Address ?? r.address ?? '';
          if (includeMobile) base['Mobile'] = r.__raw?.Mobile ?? r.mobile ?? '';
          if (includeEmail) base['Email'] = r.__raw?.Email ?? r.email ?? '';
          return base;
        });
        const wb2 = XLSX.utils.book_new();
        const ws2 = XLSX.utils.json_to_sheet(worksheetData, { origin: 'A1' });
        XLSX.utils.book_append_sheet(wb2, ws2, 'Policy Report');
        const cols = Object.keys(worksheetData[0] || {}).map((k) => ({ wch: Math.max(10, Math.min(40, k.length + 8)) }));
        ws2['!cols'] = cols;
        const filename = `policy_report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;
        XLSX.writeFile(wb2, filename);
      } catch (fallbackErr) {
        // last resort: alert user
        // eslint-disable-next-line no-console
        console.error('Failed to export XLSX', fallbackErr);
        alert('Failed to export XLSX: ' + (fallbackErr?.message ?? fallbackErr));
      }
    }
  };

  const exportToPdf = (rows) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const title = 'Policy Report';
  const includeAddress = fetchParams.includeAddress === '1' || fetchParams.includeAddress === 1 || fetchParams.includeAddress === true;
  const includeMobile = fetchParams.includeMobile === '1' || fetchParams.includeMobile === 1 || fetchParams.includeMobile === true;
  const includeEmail = fetchParams.includeEmail === '1' || fetchParams.includeEmail === 1 || fetchParams.includeEmail === true;
    // For PDF body, reuse the same finalKeys/humanize logic used for XLSX export so the PDF contains all fields
    const keySet = new Set();
    rows.forEach(r => {
      if (!r || typeof r !== 'object') return;
      Object.keys(r).forEach(k => keySet.add(k));
      if (r.__raw && typeof r.__raw === 'object') Object.keys(r.__raw).forEach(k => keySet.add(k));
    });
    const preferred = ['GroupCode', 'Area', 'LifeAssured', 'LifeAssuredName', 'PolicyNo', 'PolicyNumber', 'PolicyStatus', 'Product', 'TermPPT', 'CommencementDate', 'CommDate', 'FUPDate', 'MaturityDate', 'SumAssured', 'PaymentMode', 'NetPremium', 'Agency', 'Address', 'Mobile', 'Email', 'DOB', 'CustomerID'];
    const ordered = [];
    preferred.forEach(k => { if (keySet.has(k)) { ordered.push(k); keySet.delete(k); } });
    const rest = Array.from(keySet).sort();
    const finalKeys = ordered.concat(rest);

    const humanize = (k) => {
      if (!k) return '';
      let s = String(k).replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
      s = s.replace(/\s+/g, ' ').trim();
      return s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const headers = finalKeys.map(k => humanize(k));

    const body = rows.map(r => finalKeys.map(k => {
      if (r && Object.prototype.hasOwnProperty.call(r, k)) return r[k] ?? '';
      if (r && r.__raw && Object.prototype.hasOwnProperty.call(r.__raw, k)) return r.__raw[k] ?? '';
      const lower = k.toLowerCase();
      if (r) {
        for (const rk of Object.keys(r)) if (rk.toLowerCase() === lower) return r[rk] ?? '';
      }
      if (r && r.__raw) {
        for (const rk of Object.keys(r.__raw)) if (rk.toLowerCase() === lower) return r.__raw[rk] ?? '';
      }
      return '';
    }));
    doc.setFontSize(14);
    doc.text(title, 40, 40);
    autoTable(doc, {
      head: [headers],
      body,
      startY: 60,
      styles: { fontSize: 10, cellPadding: 6 },
      headStyles: { fillColor: [240, 240, 240] },
      theme: 'grid',
      margin: { left: 20, right: 20 },
      didDrawPage: () => {
        const page = doc.getNumberOfPages();
        doc.setFontSize(9);
        doc.text(`Page ${page}`, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 10);
      },
    });
    const filename = `policy_report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
    doc.save(filename);
  };

  const handleExportXls = (e) => {
    e?.preventDefault?.();
    exportToXlsx(flatSorted);
  };

  const handleExportPdf = (e) => {
    e?.preventDefault?.();
    handlePreviewPdf();
  };

  // cleanup object URL when modal closes / unmount
  useEffect(() => {
    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [pdfObjectUrl]);

  /* =========================
     UI (unchanged layout; hooks wired)
     ========================= */
  return (
    <div className="formContainer lg:p-4 rounded" style={{ boxShadow: '0 0 8px #ccc' }}>
      {/* Report generation overlay (PDF / long running operations) */}
      <ReportLoader visible={previewLoading || jobCreating} message={previewLoading ? 'PDF report is generating...' : (jobCreating ? 'Preparing export job...' : 'Report is generating...')} />
      <div className="p-2 mb-3" />
      <div className="flex flex-col lg:mx-5 p-4 border rounded bg-white" style={{ boxShadow: '0px 0px 12px #cac8c8' }}>
        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">{headerTitle}</h5>

        {/* EXPORT ICONS ROW */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            title="Export PDF"
            aria-label="Export PDF"
            type="button"
            className="flex flex-col items-center gap-1 p-1 "
            onClick={handleExportPdf}
            disabled={!hasAuthToken || previewResult?.isLoading || previewResult?.isFetching}
          >
            {/* <div className="w-10 h-10 border border-gray-200 rounded-sm flex items-center justify-center bg-white">
              <FaRegFilePdf />
            </div> */}
            <FaFilePdf size={28} className="text-red-600" />

            <span className="text-xs font-semibold tracking-wide">PDF</span>
          </button>

          <button
            title="Export Excel"
            aria-label="Export Excel"
            type="button"
            className="flex flex-col items-center gap-1 p-1 "
            onClick={handleExportXls}
          >
            {/* <div className="w-10 h-10 border border-gray-200 rounded-sm flex items-center justify-center bg-white">
              <PiMicrosoftExcelLogoBold className="w-5 h-5" />
            </div> */}
            <RiFileExcel2Line size={30} className="text-green-700" />

            <span className="text-xs font-semibold tracking-wide">XLS</span>
          </button>
        </div>

        {/* Auth guard message */}
        {!hasAuthToken && (
          <div className="text-sm text-red-600 mt-2 mr-4">Not signed in — please login to preview or export reports.</div>
        )}

        <div className="text-sm text-gray-500 mr-4">&nbsp;</div>

        <div className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600 mr-2">Sorting Order:</span>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortMode(opt.key)}
                  className={`text-sm px-3 py-1 rounded-md ${sortMode === opt.key ? 'bg-brandRed text-white' : 'bg-gray-100 text-gray-700'} transition`}
                  type="button"
                  aria-pressed={sortMode === opt.key}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                  Results: <strong className="text-gray-700">{displayData.length}</strong>
                </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse table-auto">
              <thead>
                {/* make header more visible: slightly larger font, bolder, and clearer border */}
                <tr className="text-left text-gray-600" style={{ background: '#f6fbff' }}>
                  {sortMode === 'groupwise' ? (
                    <>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Group Code</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Life Assured</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Area</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Policy No</th>
                      {includePolicyStatus && <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Policy Status</th>}
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Product</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Term/PPT</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Commencement Date</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">FUP Date</th>
                      {includeMaturityDate && <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Maturity Date</th>}
                      {/* Maturity Status column removed - using Policy Status instead */}
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Sum Assured</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Payment Mode</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Net Premium</th>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Agency</th>
                      {includeAddress && <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Address</th>}
                      {includeMobile && <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Mobile</th>}
                      {includeEmail && <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">Email</th>}
                    </>
                  ) : (
                    <>
                      <th className="px-2 py-3 border border-gray-300 text-center text-base font-semibold">
                        {sortMode === 'memberwise' ? 'Life Assured' : 
                         sortMode === 'productwise' ? 'Product' : 
                         sortMode === 'policyno' ? 'Policy No' : 
                         sortMode === 'fup' ? 'FUP Date' : 
                         sortMode === 'areawise' ? 'Area' : 
                         sortMode === 'maturity' ? 'Maturity Date' : 
                         sortMode === 'commencement' ? 'Commencement Date' : 
                         'Term/PPT'}
                      </th>
                      <th className="px-2 py-3 border border-gray-300">Group Code</th>
                      {sortMode !== 'areawise' && <th className="px-2 py-3 border border-gray-300">Area</th>}
                      {sortMode !== 'policyno' && <th className="px-2 py-3 border border-gray-300">Policy No</th>}
                      {includePolicyStatus && <th className="px-2 py-3 border border-gray-300">Policy Status</th>}
                      {sortMode !== 'productwise' && <th className="px-2 py-3 border border-gray-300">Product</th>}
                      {sortMode !== 'termwise' && <th className="px-2 py-3 border border-gray-300">Term/PPT</th>}
                      {sortMode !== 'commencement' && <th className="px-2 py-3 border border-gray-300">Commencement Date</th>}
                      {sortMode !== 'fup' && <th className="px-2 py-3 border border-gray-300">FUP Date</th>}
                      {includeMaturityDate && sortMode !== 'maturity' && <th className="px-2 py-3 border border-gray-300">Maturity Date</th>}
                      {/* Maturity Status column removed - using Policy Status instead */}
                      <th className="px-2 py-3 border border-gray-300">Sum Assured</th>
                      <th className="px-2 py-3 border border-gray-300">Payment Mode</th>
                      <th className="px-2 py-3 border border-gray-300">Net Premium</th>
                      <th className="px-2 py-3 border border-gray-300">Agency</th>
                      {includeAddress && <th className="px-2 py-3 border border-gray-300">Address</th>}
                      {includeMobile && <th className="px-2 py-3 border border-gray-300">Mobile</th>}
                      {includeEmail && <th className="px-2 py-3 border border-gray-300">Email</th>}
                    </>
                  )}
                </tr>
              </thead>

              <tbody>
                {loadingData ? (
                  <tr>
                    <td colSpan={totalCols} className="p-6 text-center">
                      <div className="flex items-center justify-center">
                        {/* <Loader /> */}
                                      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brandRed"></div>

                      </div>
                    </td>
                  </tr>
                ) : displayData.length === 0 ? (
                  <tr>
                    <td colSpan={totalCols} className="p-6 text-center text-sm text-gray-500">
                      {dataIsError ? (dataError?.message ?? 'Failed to load') : (isMaturityReport ? 'None of Policies are matured in given date range' : 'No records')}
                    </td>
                  </tr>
                ) : sortMode === 'groupwise' ? (
                  Object.entries(grouped).map(([groupKey, members]) => (
                    <React.Fragment key={groupKey}>
                      <tr className="bg-gray-50">
                        <td className="px-2 py-3 border border-gray-200 align-middle">
                          <button
                            onClick={() => toggleGroup(groupKey)}
                            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700"
                            aria-expanded={!!expanded[groupKey]}
                            type="button"
                          >
                            {expanded[groupKey] ? <FiChevronDown /> : <FiChevronRight />}
                            <span>{groupKey}</span>
                          </button>
                        </td>

                        <td className="border border-gray-200" colSpan={totalCols - 1} />
                      </tr>

                      {expanded[groupKey] &&
                        members.map((m, idx) => (
                          <tr key={groupKey + '_' + idx} className="hover:bg-gray-50">
                            <td className="px-2 py-3 border border-gray-200 align-top" />
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.lifeAssured}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.area}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.policyNo}</td>
                            {includePolicyStatus && (
                              <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.policyStatus}</td>
                            )}
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.product}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.termPPT}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center whitespace-nowrap">{m.commencementDate}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center whitespace-nowrap">{m.fupDate}</td>
                            {includeMaturityDate && (
                              <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center whitespace-nowrap">{m.maturityDate}</td>
                            )}
                            {/* Maturity Status cell intentionally omitted; Policy Status column will show the status */}
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.sumAssured}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.paymentMode}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.netPremium}</td>
                            <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.agency}</td>
                            {includeAddress && <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.address}</td>}
                            {includeMobile && <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.mobile}</td>}
                            {includeEmail && <td className="py-3 border border-gray-200 text-sm text-gray-700 text-center">{m.email}</td>}
                          </tr>
                        ))}
                    </React.Fragment>
                  ))
                ) : (
                  flatSorted.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700 text-center whitespace-nowrap">
                        {sortMode === 'memberwise' ? r.lifeAssured : 
                         sortMode === 'productwise' ? r.product : 
                         sortMode === 'policyno' ? r.policyNo : 
                         sortMode === 'fup' ? r.fupDate : 
                         sortMode === 'areawise' ? r.area : 
                         sortMode === 'maturity' ? r.maturityDate : 
                         sortMode === 'commencement' ? r.commencementDate : 
                         r.termPPT}
                      </td>
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.groupCode}</td>
                      {sortMode !== 'areawise' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.area}</td>}
                      {sortMode !== 'policyno' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.policyNo}</td>}
                      {includePolicyStatus && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.policyStatus}</td>}
                      {sortMode !== 'productwise' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.product}</td>}
                      {sortMode !== 'termwise' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.termPPT}</td>}
                      {sortMode !== 'commencement' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700 whitespace-nowrap">{r.commencementDate}</td>}
                      {sortMode !== 'fup' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700 whitespace-nowrap">{r.fupDate}</td>}
                      {includeMaturityDate && sortMode !== 'maturity' && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700 whitespace-nowrap">{r.maturityDate}</td>}
                      {/* Maturity Status cell intentionally omitted; Policy Status column will show the status */}
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.sumAssured}</td>
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.paymentMode}</td>
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.netPremium}</td>
                      <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.agency}</td>
                      {includeAddress && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.address}</td>}
                      {includeMobile && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.mobile}</td>}
                      {includeEmail && <td className="px-2 py-3 border border-gray-200 text-sm text-gray-700">{r.email}</td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-40">
          <div className="bg-white rounded-md w-full max-w-6xl h-[80vh] overflow-hidden shadow-lg">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-3">
                <FaRegFilePdf className="text-red-600" />
                <div>
                  <div className="font-semibold">PDF Preview</div>
                  {pdfIsDummy && <div className="text-xs text-red-600">DUMMY DATA — live data was not accessible</div>}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600 mr-4">{jobStatusText}</div>
                <button className="px-3 py-1 bg-gray-100 rounded text-sm" onClick={handleCreateExportJobAndDownload} disabled={jobCreating}>
                  {jobCreating ? 'Preparing...' : 'Download'}
                </button>
                <button
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfObjectUrl) {
                      URL.revokeObjectURL(pdfObjectUrl);
                      setPdfObjectUrl(null);
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="h-full">
              {previewResult?.isLoading || previewResult?.isFetching ? (
                <div className="flex items-center justify-center h-full">Generating preview...</div>
              ) : (
                <iframe title="PDF Preview" src={pdfObjectUrl} width="100%" height="100%" style={{ border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
//LifePolicyRegister/PolicyReport.js